import {
  ALLOW_EDIT_MESSAGE_ID,
  SUPPORT_PARAMETER_TYPES,
  O_CAN,
  O_MAP,
  O_MLOGIC,
  O_TIMER,
  N_CAN,
  N_MAP,
  N_MLOGIC,
  N_MLOGIC2,
  N_PLC,
  N_TIMER,
  O_SLOTCOPY,
  N_SLOTCOPY,
  S_MAPSLOTS,
  CARRY_ONLY,
  rowidLogicalSlotOffsets,
  rowidLogicalSlotOffsetsRV,
  VEHICLE_SUBTYPES,
  CHEC_MAX_VALUE,
  stringMaxVal,
} from 'utils/constants'
import { pick } from 'ramda'
import { inputs, getApiValue, getDisplayValue } from 'utils/inputs.js'
import { isNumeric, hasOwnPropInObj } from 'utils/utils.js'
import strings from 'utils/strings'
import { isString, isNumber } from 'utils/types'
import { SLOT_TYPES } from './logicalSlotConversions'

export function parseMLogicParameter(data) {
  if (data.hasBeenMapped) return data

  const exportData = {
    hasBeenMapped: true,
    status: data.status,
    id: data.id,
    uri: data.uri,
    description: data.description,
    supportParameterType: data.supportParameterType,
    mLogicSlotName: data.mlogic_slot_name || null,
    rowid: data.rowid || null,
    inputs: {
      E0: null,
      E1: null,
      E2: null,
      E3: null,
      E4: null,
      E5: null,

      C0: null,
      C1: null,
      C2: null,
      C3: null,
      C4: null,
      C5: null,
      C6: null,
      C7: null,
      C8: null,
      C9: null,
      C10: null,
      C11: null,
    },
    output: {
      ls: data.cout_ls,
      taskId: data.cout_task_id,
      trueValue: data.cout_true,
      falseValue: data.cout_false,
      defaultValue: data.cout_default,
    },
  }

  Object.keys(exportData.inputs).forEach((key) => {
    const lKey = key.toLowerCase()
    exportData.inputs[key] = {
      op: data[`${lKey}_op`],
      value: getDisplayValue(data[`${lKey}_ls`], data[`${lKey}_value`]),
      ls: data[`${lKey}_ls`],
    }
  })

  return exportData
}

export function slotIsOpen(op) {
  return op === '0'
}

export function slotIsShort(op) {
  return op === '1'
}

const spTypeDefnitions = [
  O_CAN,
  N_CAN,
  O_MAP,
  N_MAP,
  N_MLOGIC,
  N_MLOGIC2,
  O_TIMER,
  N_TIMER,
  N_PLC,
  S_MAPSLOTS,
]
export const getTitle = (logicalSlot, spList = []) => {
  const definitions = spList.filter(
    (sParam) => sParam && spTypeDefnitions.includes(sParam.supportParameterType)
  )
  let definition

  // see if there's a specific definition for it
  definition = definitions.find((def) => {
    const { spType, rowid } = translateLSValueToSPTypeAndRowId(logicalSlot)
    return (
      def.logical_slot === logicalSlot ||
      def.timer_slot === logicalSlot ||
      // Possibly an unnecessary guard, but make sure the field names are associated with the proper SP type before returning the SP
      (isSlotCopy(def.supportParameterType) &&
        def.destination === logicalSlot) ||
      (def.supportParameterType === S_MAPSLOTS &&
        def.slot_name === logicalSlot) ||
      translateNativeRowIdToLSValue(def) === logicalSlot ||
      (def.supportParameterType === spType &&
        (def.rowid === rowid || def.row_id === rowid)) ||
      def.logicalSlot === logicalSlot
    )
  })

  if (!definition && spList.length) {
    definition = spList.find(
      (sParam) => sParam && sParam.mlogic_slot_name === logicalSlot
    )
  }

  // fall back to default
  if (!definition && isNumeric(logicalSlot)) {
    definition = inputs.find((i) => i.value === Number(logicalSlot))
  }

  if (!definition && isString(logicalSlot)) {
    definition = inputs.find((i) => i.logicalSlot === logicalSlot)
  }

  const title =
    definition && definition.description !== ''
      ? definition.description
      : logicalSlot
  return { title, def: definition }
}

const determineUnit = (def, value) => {
  if (!def || !def.unit) {
    return ''
  }
  if (typeof def.unit === 'function') {
    return def.unit(value)
  }
  return def.unit
}

export const mapMLogic = (mLogic, siblingSPsList) => {
  const { ls, defaultValue, trueValue, falseValue } = mLogic.output
  // examine each input in the mlogic (or mlogic2). They are properties, not an array, so we need to enumerate them.
  const newInputs = Object.entries(mLogic.inputs).reduce(
    (inputsCollection, [key, input]) => {
      // eslint-disable-next-line no-shadow
      const { ls, op, value } = input
      inputsCollection[key] = input

      // if this input is simply Open or Short/Closed, then there's nothing left to do
      if (slotIsOpen(op) || slotIsShort(op)) {
        inputsCollection[key].displayText = undefined
        return inputsCollection
      }

      const { title, def } = getTitle(ls, siblingSPsList)

      const isSmartPlex =
        def &&
        (({ supportParameterType, type, physical_slot }) => {
          if (!Number.isNaN(ls) && Number(ls) === SLOT_TYPES.SMARTPLEX) {
            return true
          }
          const isMap = [O_MAP, N_MAP].includes(supportParameterType)
          if (!isMap) {
            return false
          }
          if (type === 'M') {
            // if (['M', '7'].includes(type)) { // TODO: Confirm that this is the correct implementation of the business rules (VSO bug 28195)
            return physical_slot.includes('DYNAMIC SMPLX OCAN')
          }
          if (['9', '10', '11', 'SA', 'SB', 'SI'].includes(type)) {
            return [
              '273',
              '274',
              '275',
              '276',
              '277',
              '278',
              '279',
              '280',
              '281',
              '282',
              '283',
              '284',
              '289',
              '290',
              '291',
              '292',
              '293',
              '294',
              '295',
              '296',
              '297',
              '298',
              '299',
              '300',
              '305',
              '306',
              '307',
              '308',
              '309',
              '310',
              '311',
              '312',
              '313',
              '314',
              '315',
              '316',
              '321',
              '322',
              '323',
              '324',
              '325',
              '326',
            ].includes(physical_slot)
          }
          return false
        })(def)

      inputsCollection[key].displayText = {
        title,
        unit: determineUnit(def, value),
        semanticDisplay:
          def && def.semanticDisplay
            ? def.semanticDisplay(op, value)
            : undefined,
        isSmartPlex,
      }
      return inputsCollection
    },
    {}
  )

  const getSemanticBool = (val) => String(Number(val) === 1).toUpperCase()

  const outputDisplayValues = (lsValue) => {
    const { title, def } = getTitle(lsValue, siblingSPsList)
    const displayValueIsSemantic = Boolean(def && def.outputDisplay)

    const isTimer = ((defValue) => {
      if (!defValue) return false
      return (
        !!defValue.timer_slot ||
        (defValue.supportParameterType &&
          [O_TIMER, N_TIMER].includes(defValue.supportParameterType)) ||
        (defValue.value && defValue.value >= 151 && defValue.value <= 160) ||
        (defValue.rowid && defValue.rowid >= 151 && defValue.rowid <= 160)
      )
    })(def)

    let defaultDisplay = ''
    let trueDisplay = ''
    let falseDisplay = ''

    if (
      [lsValue, defaultValue, trueValue, falseValue].every(
        (thing) => String(thing) === String(CHEC_MAX_VALUE)
      )
    ) {
      defaultDisplay = CARRY_ONLY
      trueDisplay = CARRY_ONLY
      falseDisplay = CARRY_ONLY
    } else if (displayValueIsSemantic) {
      defaultDisplay = def.outputDisplay(defaultValue)
      trueDisplay = def.outputDisplay(trueValue)
      falseDisplay = def.outputDisplay(falseValue)
    } else if (isTimer) {
      defaultDisplay = `${title} = ${getTimerOutputSemanticValue(
        defaultValue,
        def
      )}`
      trueDisplay = `${title} = ${getTimerOutputSemanticValue(trueValue, def)}`
      falseDisplay = `${title} = ${getTimerOutputSemanticValue(
        falseValue,
        def
      )}`
    } else {
      defaultDisplay = `${title} = ${getSemanticBool(defaultValue)}`
      trueDisplay = `${title} = ${getSemanticBool(trueValue)}`
      falseDisplay = `${title} = ${getSemanticBool(falseValue)}`
    }

    return {
      displayValueIsSemantic,
      defaultDisplay,
      trueDisplay,
      falseDisplay,
      isTimer,
    }
  }

  const newOutput = {
    ...mLogic.output,
    ...outputDisplayValues(ls),
  }

  return {
    ...mLogic,
    inputs: newInputs,
    output: newOutput,
  }
}

export const mapSlotCopy = (slotcopy, siblingSPsList) => {
  const sourceTitleObj = getTitle(slotcopy.source, siblingSPsList)
  const destinationTitleObj = getTitle(slotcopy.destination, siblingSPsList)

  return {
    ...slotcopy,
    sourceTitle: sourceTitleObj.title,
    destinationTitle: destinationTitleObj.title,
  }
}

export const flattenMLogicForBatchImport = (data) => {
  const type = data.supportParameterType
  return isMLogic(type) ? mLogicForBatchImport(data) : data
}

const reformattedInputs = (data) => {
  const newObj = Object.entries(data.inputs).reduce(
    (accum, [key, val]) => ({
      ...accum,
      [key]: {
        operator: val.op,
        value: val.value,
        logicalSlot: val.ls,
      },
    }),
    {}
  )
  return newObj
}

export const denormalizeMLogicBatchImports = (sp) => {
  const unpackedInputs = Object.entries(sp.inputs).reduce((obj, [key, val]) => {
    const newKey = key.toLowerCase()
    return {
      ...obj,
      [`${newKey}_ls`]: val.logicalSlot,
      [`${newKey}_op`]: val.operator,
      [`${newKey}_value`]: val.value,
    }
  }, {})

  return {
    ...(sp.supportParameterType === O_MLOGIC
      ? { mlogic_slot_name: sp.mLogicSlotName }
      : { rowid: sp.rowid }),

    ...pick(['status', 'id', 'uri', 'description', 'supportParameterType'], sp),
    ...unpackedInputs,
    cout_default: sp.outputDefault,
    cout_false: sp.outputFalse,
    cout_ls: sp.logicalSlot,
    cout_task_id: sp.taskId,
    cout_true: sp.outputTrue,
  }
}

export const mLogicForBatchImport = (sp) => ({
  status: sp.status,
  id: sp.id,
  uri: sp.uri,
  description: sp.description,
  supportParameterType: sp.supportParameterType,
  logicalSlot: sp.output.ls,
  taskId: sp.output.taskId,
  outputTrue: sp.output.trueValue,
  outputFalse: sp.output.falseValue,
  outputDefault: sp.output.defaultValue,
  rowid: sp.rowid,
  inputs: reformattedInputs(sp),
})

export function flattenSP(data) {
  const type = data.supportParameterType
  if (isMLogic(type)) {
    return flattenMLogic(data)
  }
  if (isSlotCopy(type)) {
    return flattenSlotCopy(data)
  }
  return data
}

// MLogics are used in memory as fairly dimensional structures, but are passed to and from the API as
// totally flat.
export function flattenMLogic(data) {
  let result = {
    status: data.status,
    id: data.id,
    uri: data.uri,
    description: data.description,
    supportParameterType: data.supportParameterType,
    cout_ls: data.logicalSlot,
    cout_task_id: data.taskId,
    cout_true: data.outputTrue,
    cout_false: data.outputFalse,
    cout_default: data.outputDefault,
  }

  // different types of mlogics have some different fields
  if (data.supportParameterType.startsWith(N_MLOGIC)) {
    result = {
      ...result,
      rowid: data.rowid,
    }
  }
  if (data.supportParameterType === O_MLOGIC) {
    result = {
      ...result,
      mlogic_slot_name: data.mLogicSlotName,
    }
  }

  // the inputs are the tricky part. Each input is in memory as:
  // param: {
  //     inputs: {
  //         C4: {
  //             operator: operator,
  //             logicalSlot: logical slot,
  //             value: value
  //         }
  //     }
  // }
  // when all is said and done, the above structure should become 3 fields on the flat data structure:
  // c4_op,
  // c4_ls,
  // c4_value
  Object.keys(data.inputs).forEach((key) => {
    if (hasOwnPropInObj(data.inputs, key)) {
      const lkey = key.toLowerCase()
      result = {
        ...result,
        [`${lkey}_ls`]: data.inputs[key].logicalSlot,
        [`${lkey}_op`]: data.inputs[key].operator,
        [`${lkey}_value`]: data.inputs[key].value,
      }
    }
  })

  return result
}

export function flattenSlotCopy(data) {
  // https://codeburst.io/use-es2015-object-rest-operator-to-omit-properties-38a3ecffe90
  // eslint-disable-next-line no-unused-vars
  const { sourceTitle, destinationTitle, ...apiData } = data
  return apiData
}

export function defaultMLogic(type) {
  return {
    uri: 'undefined-tmp',
    description: 'Add Description',
    mLogicSlotName: 'Add Slot Name',
    rowid: 0,
    supportParameterType: type || SUPPORT_PARAMETER_TYPES.O_MLOGIC,
    inputs: {
      E0: {
        ls: stringMaxVal,
        op: '1',
        value: stringMaxVal,
      },
      E1: {
        ls: stringMaxVal,
        op: '1',
        value: stringMaxVal,
      },
      E2: {
        ls: stringMaxVal,
        op: '1',
        value: stringMaxVal,
      },
      E3: {
        ls: stringMaxVal,
        op: '0',
        value: stringMaxVal,
      },
      E4: {
        ls: stringMaxVal,
        op: '0',
        value: stringMaxVal,
      },
      E5: {
        ls: stringMaxVal,
        op: '0',
        value: stringMaxVal,
      },
      C0: {
        ls: stringMaxVal,
        op: '1',
        value: stringMaxVal,
      },
      C1: {
        ls: stringMaxVal,
        op: '1',
        value: stringMaxVal,
      },
      C2: {
        ls: stringMaxVal,
        op: '1',
        value: stringMaxVal,
      },
      C3: {
        ls: stringMaxVal,
        op: '1',
        value: stringMaxVal,
      },
      C4: {
        ls: stringMaxVal,
        op: '1',
        value: stringMaxVal,
      },
      C5: {
        ls: stringMaxVal,
        op: '1',
        value: stringMaxVal,
      },
      C6: {
        ls: stringMaxVal,
        op: '0',
        value: stringMaxVal,
      },
      C7: {
        ls: stringMaxVal,
        op: '0',
        value: stringMaxVal,
      },
      C8: {
        ls: stringMaxVal,
        op: '0',
        value: stringMaxVal,
      },
      C9: {
        ls: stringMaxVal,
        op: '0',
        value: stringMaxVal,
      },
      C10: {
        ls: stringMaxVal,
        op: '0',
        value: stringMaxVal,
      },
      C11: {
        ls: stringMaxVal,
        op: '0',
        value: stringMaxVal,
      },
    },
    output: {
      ls: stringMaxVal,
      taskId: stringMaxVal,
      trueValue: stringMaxVal,
      falseValue: stringMaxVal,
      defaultValue: stringMaxVal,
    },
  }
}

export function spLengthToBits(length) {
  // This would be just Math.pow(2, length) but a length of 2 needs to be 3 bits which makes every length after 2 actually 1 less than the length provided
  let bits = 0
  if (length === 2) {
    bits = 3
  } else if (length > 2) {
    bits = 2 ** (length - 1)
  } else {
    bits = 2 ** length
  }

  return `${bits} ${bits === 1 ? 'bit' : 'bits'}`
}
export function isMLogic(spType) {
  return [O_MLOGIC, N_MLOGIC, N_MLOGIC2].indexOf(spType) > -1
}

export function isSlotCopy(spType) {
  return [O_SLOTCOPY, N_SLOTCOPY].indexOf(spType) > -1
}
const convertMLogicInputToApi = (supportParameter) => {
  const supportParamInputs = supportParameter.inputs
  if (!supportParamInputs) return supportParameter // it's probably already been converted
  const apiInputs = Object.entries(supportParamInputs).reduce(
    (obj, [key, input]) => {
      const { logicalSlot, value } = input

      const apiValue = getApiValue(logicalSlot, value)

      return {
        ...obj,
        [key]: {
          ...input,
          value: apiValue,
        },
      }
    },
    {}
  )

  return { ...supportParameter, inputs: apiInputs }
}

const FIRST_M2_TIMER_SLOT_ID = 151
const LAST_M2_TIMER_SLOT_ID = 160
const LAST_RV_TIMER_SLOT_ID = 270

// M2 && B2/S2 timer slots 151 - 160
// RV timer slots 261 - 270
function isTimerSlotId(value) {
  return (
    isNumber(parseInt(value)) &&
    parseInt(value) >= FIRST_M2_TIMER_SLOT_ID &&
    parseInt(value) <= LAST_M2_TIMER_SLOT_ID
  )
}

function shiftSlotIdForRVTimers(id) {
  if (parseInt(id.slice(-1)) === 0) return LAST_RV_TIMER_SLOT_ID
  return `26${id.slice(-1)}`
}

function convertSlotIdsForRV(acc, key, value) {
  if (key.slice(-2) === 'ls') {
    // shift timers
    if (isTimerSlotId(value)) {
      return {
        ...acc,
        [key]: shiftSlotIdForRVTimers(value),
      }
    }
  }

  return {
    ...acc,
    [key]: value,
  }
}

export function spApiData(supportParameter, rpType = VEHICLE_SUBTYPES.M2) {
  const { supportParameterType } = supportParameter

  if (isMLogic(supportParameterType)) {
    const convertedMLogic = convertMLogicInputToApi(supportParameter)
    const sp = denormalizeMLogicBatchImports(convertedMLogic)
    if (rpType === VEHICLE_SUBTYPES.RV) {
      const adjustedForRV = Object.entries(sp).reduce(
        (acc, [key, value]) => convertSlotIdsForRV(acc, key, value),
        {}
      )
      return adjustedForRV
    }
    return sp
  }
  if (isSlotCopy(supportParameterType)) {
    return flattenSP(supportParameter)
  }
  return supportParameter
}

export function getSlotName(supportParameter) {
  switch (supportParameter.supportParameterType) {
    case O_CAN:
      return supportParameter.can_slot
    case O_MLOGIC:
    case N_MLOGIC:
    case N_MLOGIC2:
      return supportParameter.mlogic_slot_name
    case O_TIMER:
      return supportParameter.timer_slot
    default:
      return 'Support parameter'
  }
}

export function validateSuppliedValues(supportParameter) {
  const errorMessages = []

  if (!supportParameter.description) {
    errorMessages.push(strings.service_description_error)
  }

  switch (supportParameter.supportParameterType) {
    case O_MLOGIC:
    case N_MLOGIC:
    case N_MLOGIC2:
    case N_PLC:
    case N_SLOTCOPY:
    case O_SLOTCOPY:
    case N_MAP:
    case O_MAP:
      break
    case O_CAN:
    case N_CAN:
      if (
        !Number.isInteger(Number(supportParameter.update_rate)) ||
        Number(supportParameter.update_rate) < 0 ||
        Number(supportParameter.update_rate) > 65535
      ) {
        errorMessages.push(
          strings.enter_number_between_0_and_65535(
            strings.milliseconds_lowercase
          )
        )
      }
      if (
        supportParameter.pgn === ALLOW_EDIT_MESSAGE_ID &&
        (!Number.isInteger(Number(supportParameter.message_id)) ||
          Number(supportParameter.message_id) < 0 ||
          Number(supportParameter.message_id) > 255)
      ) {
        errorMessages.push(
          strings.invalid_message_id(supportParameter.message_id)
        )
      }
      break
    case N_TIMER:
    case O_TIMER:
      if (
        !Number.isInteger(Number(supportParameter.max)) ||
        Number(supportParameter.max) < 0 ||
        Number(supportParameter.max) > 65535
      ) {
        errorMessages.push(strings.invalid_timer_value(supportParameter.max))
      }
      if (
        !Number.isInteger(Number(supportParameter.reset)) ||
        Number(supportParameter.max) < 0 ||
        Number(supportParameter.max) > 65535
      ) {
        errorMessages.push(strings.invalid_reset_value(supportParameter.reset))
      }
      break
    default:
      throw new Error(
        strings.unable_to_validate_sptype(supportParameter.supportParameterType)
      )
  }

  return errorMessages
}

export function getMaxRowIDValue(spType) {
  // Ranges provied by Chad.
  // Current implementation of N_PLC is not utilizing a range check
  // NMap range check not in input box
  switch (spType) {
    case N_CAN:
      return 49
    case N_MAP:
      return 699
    case N_MLOGIC:
      return 39
    case N_MLOGIC2:
      return 79
    case N_SLOTCOPY:
      return 31
    case N_PLC:
      return 191
    case N_TIMER:
      return 9
    default:
      return CHEC_MAX_VALUE
  }
}

export const spliceId = (slotName) => {
  const idx = slotName.lastIndexOf('-')
  return idx > -1 ? slotName.slice(0, idx) : slotName
}

export function getSemanticOutputValue(val, def) {
  if ('timer_slot' in def) return getTimerOutputSemanticValue(val)
  return ''
}

export function getTimerOutputSemanticValue(val) {
  switch (val) {
    case '0':
      return 'Off'
    case '1':
      return 'Single run timer'
    case '2':
      return 'Repeating timer'
    case '65535':
      return 'Disabled'
    default:
      return 'Invalid'
  }
}

export const isNativeSP = (supportParameterType) => {
  const nativeSPs = [N_CAN, N_MAP, N_MLOGIC, N_MLOGIC2, N_SLOTCOPY, N_TIMER]
  return nativeSPs.includes(supportParameterType)
}

export const translateNativeRowIdToLSValue = ({
  supportParameterType,
  rowid,
  row_id,
  platform,
}) => {
  const numId = Number(rowid || row_id)
  if (supportParameterType === N_MLOGIC) {
    return String(
      numId < 20 // N_MLOGIC rowIds 0-19
        ? numId + rowidLogicalSlotOffsets.N_MLOGIC[0] // become 131-150
        : numId + rowidLogicalSlotOffsets.N_MLOGIC[1]
    ) // rowids 20-39 become 340-359
  }
  if (supportParameterType === N_MLOGIC2) {
    return String(numId + rowidLogicalSlotOffsets.N_MLOGIC2) // N_MLOGIC2 rowids 0-79 become 375-454
  }
  if (supportParameterType === N_TIMER) {
    if (platform === VEHICLE_SUBTYPES.RV) {
      return String(numId + rowidLogicalSlotOffsetsRV.N_TIMER)
    }
    return String(numId + rowidLogicalSlotOffsets.N_TIMER) // N_TIMER rowids 0-9 become 151-160
  }
  if (supportParameterType === O_TIMER) {
    return String(numId + rowidLogicalSlotOffsets.O_TIMER) // no idea the O_TIMER rowids
  }
  return rowid // no translation required
}

export const translateLSValueToSPTypeAndRowId = (ls) => {
  // this conforms to the M2 list only right now :(
  const numLs = Number(ls)

  if (numLs >= 151 && numLs <= 160) {
    return {
      spType: N_TIMER,
      rowid: String(numLs - rowidLogicalSlotOffsets.N_TIMER),
    }
  }

  if (numLs >= 375 && numLs <= 454) {
    return {
      spType: N_MLOGIC2,
      rowid: String(numLs - rowidLogicalSlotOffsets.N_MLOGIC2),
    }
  }

  if (numLs >= 131 && numLs <= 150) {
    return {
      spType: N_MLOGIC,
      rowid: String(numLs - rowidLogicalSlotOffsets.N_MLOGIC[0]),
    }
  }
  if (numLs >= 340 && numLs <= 359) {
    return {
      spType: N_MLOGIC,
      rowid: String(numLs - rowidLogicalSlotOffsets.N_MLOGIC[1]),
    }
  }

  return {
    spType: '',
    rowid: null,
  }
}

export const containsInvalidSPs = (key, invalidSPValues = []) =>
  invalidSPValues.some((sp) => sp.id === key)

export const SMARTPLEX_CONTENT_NAMES = [
  'DYNAMIC SMPLX',
  'DYNAMIC ONOFF LATCHING SW',
  'DYNAMIC REMOTE PTO CTRL',
]
